import React, { useState, useEffect } from "react";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import _ from "lodash";
import moment from "moment";
import Checkbox from "components/CheckBox";
import { useSelector, useDispatch } from "react-redux";
import Button from "components/Button";
import Select from "components/Select";
import TextField from "components/TextField";
import COLORS from "shared/constants/colors";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import { getSKUFromProductId, isSub } from "selectors/products";
import OrderInformation from "pages/order-details/OrderInformation";
import OrderPickUp from "pages/order-details/OrderPickUp";
import CustomerInformation from "pages/order-details/CustomerInformation";
import PaymentErrorDialog from "pages/order-details/PaymentErrorDialog";
import Stripe from "components/Stripe";
import Image from "components/Image";
import Slider from "react-slick";
import { isDecorationNotNeeded } from "selectors/products";
import EcoIcon from "@material-ui/icons/Eco";
import InboxIcon from "@material-ui/icons/Inbox";
import { USD_TO_CAD_CONVERSION_RATE } from "constants/currency";
import {
  marketPlaceOrderDetails,
  addLineItem,
  searchAllSubmittedOrders,
} from "actions/orders";
import {
  stripeCharge,
  getAllCards,
  removeCard,
  calculateTax,
  paypalCharge,
} from "actions/stripe";
import {
  submitOrderToAU,
  submitOrderToCanada,
  submitOrderToUSA,
  updateOrderStatus,
  saveOrderData,
  orderSubmitNotification,
  orderDurasoftNotification,
  orderTuffPrintTransferNotification,
  orderAOPNotification,
  updateOrderPackingMode,
  saveAllCosts,
  submitOrderToEU,
} from "actions/orders";
import { json2xml } from "utils/jsonToXmlParser";
import {
  BemerchfullButton,
  Container,
  OrderDetail,
  Heading,
  Panel,
  MoreDetail,
  OrderTotal,
  Total,
  ShippingMessage,
  ProductImage,
  SlickContainer,
  PackingOptions,
  PackingOption,
  ColorField,
} from "styles/pages/order-details/orderSummary";

const XMLParser = require("react-xml-parser");

const settings = {
  dots: true,
  infinite: false,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
};

export const getFulfillmentCost = (order = {}) => {
  const { OrderLines } = order;
  if (OrderLines?.length) {
    const { OrderLineItems } = OrderLines[0];
    let fulfillmentCost = 0;

    for (let i = 0; i < OrderLineItems?.length; i++) {
      fulfillmentCost += OrderLineItems[i]?.FulfillmentCost;
    }

    return Number(fulfillmentCost.toFixed(2)) || 0;
  }
};

export const getFulfillmentCostWithTax = (
  order = {},
  taxInfo = [],
  printLabel
) => {
  const { OrderLines } = order;
  if (OrderLines?.length) {
    const { OrderLineItems } = OrderLines[0];
    let fulfillmentCost = 0;

    for (let i = 0; i < OrderLineItems?.length; i++) {
      let fCost = OrderLineItems[i]?.FulfillmentCost;

      if (printLabel) {
        fCost += 2;
      }

      const taxOnItem = getTaxCost(taxInfo, order, OrderLineItems[i].Category);
      const taxOnFulfillment = Number(((fCost * taxOnItem) / 100).toFixed(2));

      fulfillmentCost += fCost + taxOnFulfillment;
    }

    return Number(fulfillmentCost.toFixed(2)) || 0;
  }
};

const getOrderLineTax = (order = {}, taxInfo = []) => {
  const { OrderLines } = order;
  if (OrderLines?.length) {
    const { OrderLineItems } = OrderLines[0];
    let tax = 0;

    for (let i = 0; i < OrderLineItems?.length; i++) {
      const fCost = OrderLineItems[i]?.FulfillmentCost;
      const taxOnItem = getTaxCost(taxInfo, order, OrderLineItems[i].Category);
      const taxOnFulfillment = Number(((fCost * taxOnItem) / 100).toFixed(2));

      tax += taxOnFulfillment;
    }

    return Number(tax.toFixed(2)) || 0;
  }
};

export const isVatIncluded = (tax = [], order) => {
  const selectedPrintProviderTaxInfo = tax.find(
    (c) => c?.PrintProvider === order?.PrintProvider
  );

  if (selectedPrintProviderTaxInfo) {
    const selectedDestinationCountry =
      selectedPrintProviderTaxInfo?.TaxCharges?.find(
        (c) => c?.ShipppingDestinationCountry === order?.CountryString
      );
    if (selectedDestinationCountry) {
      const selectedTaxCharge = selectedDestinationCountry?.TaxCharges?.find(
        (c) => c?.ShippingState === order?.StateProvince
      );

      return selectedTaxCharge?.VatIncluded || false;
    }
    return false;
  }
  return false;
};

export const getTaxCost = (tax = [], order, category) => {
  const isKidOrInfant = category === "Kids" || category === "Infant";
  const country = order?.CountryString;
  const state = order?.StateProvince;

  if (order?.PrintProvider === "EU") return 23;

  if (order?.PrintProvider === "AU") return 10;

  if (order?.PrintProvider === "US" && order?.CountryString == "US") return 8;

  if (isKidOrInfant && country == "CA" && state == "ON") {
    return 5;
  } else if (isKidOrInfant && country == "CA" && state == "NS") {
    return 5;
  } else if (isKidOrInfant && country == "CA" && state == "PE") {
    return 6;
  }

  const selectedPrintProviderTaxInfo = tax.find(
    (c) => c?.PrintProvider === order?.PrintProvider
  );

  if (selectedPrintProviderTaxInfo) {
    const selectedDestinationCountry =
      selectedPrintProviderTaxInfo?.TaxCharges?.find(
        (c) => c?.ShipppingDestinationCountry === country
      );
    if (selectedDestinationCountry) {
      const selectedTaxCharge = selectedDestinationCountry?.TaxCharges?.find(
        (c) => c?.ShippingState === state
      );
      return selectedTaxCharge?.TaxCharge || 0;
    }
    return 0;
  }
  return 0;
};

const OrderSummary = ({
  order = {},
  orderPrintProviders,
  shippingCostInfo,
  refresh,
  machineStatus,
  setOrderPickUp,
  setOrder,
}) => {
  const dispatch = useDispatch();
  const currency = useSelector((state) => state?.currency?.value);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [cards, setCards] = useState(undefined);
  const [taxInfo, setTaxInfo] = useState(undefined);
  const [paymentError, setPaymentError] = useState(undefined);
  const [packingMode, setPackingMode] = useState(undefined);
  const countries = useSelector((state) => state?.countries?.data);
  const [lynxOrderData, setLynxOrderData] = useState(undefined);
  const isOrderAwaitingPayment = order?.Status === "AwaitingPayment";
  const isOrderInProduction = order?.Status === "InProduction";

  const { OrderLines: orderLines } = order;
  const { OrderLineItems: orderLineItems } = orderLines?.length
    ? orderLines[0]
    : {};
  const products = useSelector((state) => state?.products?.data);
  const token = useSelector((state) => state?.auth?.profile?.token);
  const auth = useSelector((state) => state?.auth);
  const {
    profile: { paymentSkip, group },
  } = auth;

  const emailid = auth?.profile?.emailid;

  const isSamAccount = emailid === "sam@bemerchful.com";

  const updatePackingRequest = async (packingMode) => {
    await dispatch(
      updateOrderPackingMode({
        user: emailid,
        orderId: order?.ID,
        packingMode,
      })
    );
  };

  useEffect(() => {
    const fetchLynxOrderData = async () => {
      if (!lynxOrderData) {
        const d = await dispatch(
          searchAllSubmittedOrders({
            ReceiptID: order?.ReceiptID,
          })
        );

        if (d.error) {
          return;
        }

        if (d?.orders?.length > 0) {
          setLynxOrderData(d?.orders[0]);
        }
      }
    };
    fetchLynxOrderData();
  }, [dispatch, lynxOrderData]);

  useEffect(() => {
    const fetchCards = async () => {
      if (!cards) {
        const cards = await dispatch(
          getAllCards({
            user: emailid,
          })
        );

        if (cards.error) {
          return;
        }

        setCards(cards);
      }
    };
    fetchCards();
  }, [dispatch, setCards, emailid, cards]);

  useEffect(() => {
    const fetchTaxInfo = async () => {
      if (!cards) {
        const tax = await dispatch(calculateTax());

        if (tax.error) {
          return;
        }

        setTaxInfo(tax);
      }
    };
    fetchTaxInfo();
  }, [dispatch, emailid]);

  useEffect(() => {
    const updatePacking = async () => {
      await updatePackingRequest("normal");
      refresh();
    };

    if (!order?.PackingMode) {
      updatePacking();
    } else {
      setPackingMode(order?.PackingMode);
    }
  }, [order]);

  useEffect(async () => {
    if (isSamAccount) {
      const orderClone = _.clone(order);
      const { OrderLines: orderLines } = orderClone;
      const { OrderLineItems: orderLineItems } = orderLines?.length
        ? orderLines[0]
        : [];

      for (let i = 0; i < orderLineItems.length; i++) {
        const lineItem = orderLineItems[i];

        const payload = {
          user: emailid,
          marketPlace: orderClone?.Marketplace,
          lineItemArrId: lineItem?.ID,
          productId: lineItem?.StoreProductID,
          shopName: orderClone?.Store?.ShopName,
          details: lineItem?.Details,
          ListingID: orderLines[0]?.ListingID,
        };
        const response = await dispatch(marketPlaceOrderDetails(payload));
        lineItem.MockupSrc = response?.imgSrc;
      }
      setOrder(() => orderClone);
    }
  }, [emailid]);

  const handlingCost =
    emailid === "support@gobardown.com" && order?.PrintProvider === "CA"
      ? 3
      : 0;

  const shippingCost =
    emailid === "support@gobardown.com" && order?.PrintProvider === "CA"
      ? 0
      : Number(shippingCostInfo?.cost) || 0;
  const taxPercentageOnOrder = getTaxCost(taxInfo, order);
  const shippingTaxCost = Number(
    ((shippingCost * taxPercentageOnOrder) / 100).toFixed(2)
  );
  const finalShippingCost = Number(
    (shippingCost + shippingTaxCost + (packingMode === "eco" ? 1 : 0))?.toFixed(
      2
    )
  );
  const totalOrderCostInUSD = Number(
    (
      finalShippingCost +
      handlingCost +
      getFulfillmentCostWithTax(order, taxInfo, auth?.profile?.printLabel)
    ).toFixed(2)
  );

  const subTotal = getFulfillmentCostWithTax(
    order,
    taxInfo,
    auth?.profile?.printLabel
  );

  const grandTotalInUsd = Number(
    subTotal + finalShippingCost + handlingCost
  ).toFixed(2);

  let totalQuantity = 0;

  for (let i = 0; i < orderLineItems.length; i++) {
    const o = orderLineItems[i];
    totalQuantity += +o?.OliQuantity;
  }

  const saveOrderCost = async () => {
    let totalTaxOnLineItems = 0;
    let fulfillmentCostWithoutTax = 0;

    for (let i = 0; i < orderLineItems.length; i++) {
      const o = orderLineItems[i];
      const fulfillmentCost = o?.FulfillmentCost;

      const taxOnItem = getTaxCost(taxInfo, order, o?.Category);

      const taxOnFulfillment = Number(
        ((fulfillmentCost * taxOnItem) / 100).toFixed(2)
      );

      totalTaxOnLineItems += taxOnFulfillment;
      fulfillmentCostWithoutTax += fulfillmentCost;
    }

    const payload = {
      orderId: order?.ID,
      user: emailid,
      allCost: {
        totalFullfillmentCost: subTotal,
        shippingCost,
        shippingTaxCost,
        finalShippingCost,
        totalOrderCostInUSD,
        totalOrderCostInCAD: Number(
          (totalOrderCostInUSD * USD_TO_CAD_CONVERSION_RATE)?.toFixed(2)
        ),
        totalTaxOnLineItems,
        fulfillmentCostWithoutTax,
      },
    };
    await dispatch(saveAllCosts(payload));
  };

  return (
    <>
      <ShippingMessage>
        Any claims for misprinted/damaged/defective items must be submitted
        within 30 days after the product has been received. For packages lost in
        transit, all claims must be submitted no later than 30 days after the
        estimated delivery date. Claims deemed an error on our part are covered
        at our expense. For lost packages that are not caused by our error, we
        will make our best effort to claim the loss on your behalf and re-print
        your order if successful.
      </ShippingMessage>
      <Container>
        <OrderDetail>
          <Heading>Order Details</Heading>
          {order?.Status === "AwaitingPayment" && (
            <PackingOptions singleBox={totalQuantity !== 1}>
              <PackingOption
                selected={packingMode === "normal"}
                onClick={async () => {
                  if (packingMode !== "normal") {
                    setPackingMode("normal");
                    await updatePackingRequest("normal");
                    refresh();
                  }
                }}
              >
                <InboxIcon />
                <div>Normal Packing</div>
              </PackingOption>
              {totalQuantity === 1 && (
                <PackingOption
                  eco={true}
                  selected={packingMode === "eco"}
                  onClick={async () => {
                    if (packingMode !== "eco") {
                      setPackingMode("eco");
                      await updatePackingRequest("eco");
                      refresh();
                    }
                  }}
                >
                  <EcoIcon />
                  <div>Eco-Bag Packing ($1 USD)</div>
                </PackingOption>
              )}
            </PackingOptions>
          )}
          <Panel>
            {order?.Status === "AwaitingPayment" ? (
              paymentSkip ? (
                <Button
                  containerStyle={{
                    backgroundColor: COLORS.GREEN,
                    color: COLORS.WHITE,
                    padding: "7px 5px",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    borderRadius: "50px",
                    minWidth: "100%",
                  }}
                  onClick={async () => {
                    await saveOrderCost();
                    const xml = json2xml(order, products, emailid, auth);

                    const submitOrderResponse =
                      order?.PrintProvider === "CA"
                        ? await dispatch(submitOrderToCanada(xml, token))
                        : await dispatch(submitOrderToUSA(xml, token));

                    if (
                      (order?.PrintProvider === "EU" &&
                        submitOrderResponse?.status !== 200) ||
                      (order?.PrintProvider === "AU" &&
                        submitOrderResponse?.status === "failed") ||
                      (order?.PrintProvider === "NY" &&
                        submitOrderResponse?.status === "error")
                    ) {
                      alert(
                        "Payment completed but due to some technical problem order didnot went to production. Please contact developer@bigoventees.com"
                      );
                      return;
                    }

                    const parseOrderResponse =
                      order?.PrintProvider === "AU" ||
                      order?.PrintProvider === "EU"
                        ? true
                        : new XMLParser().parseFromString(submitOrderResponse);

                    const errorCode =
                      order?.PrintProvider === "AU" ||
                      order?.PrintProvider === "EU"
                        ? null
                        : parseOrderResponse.getElementsByTagName("ErrorCode");

                    if (errorCode?.length) {
                      if (errorCode[0].value != 0) {
                        alert(
                          "Error with the order, please contact Big Oven Tees for more details at info@bigoventees.com"
                        );
                        return;
                      }
                    }
                    const orderStatus = await dispatch(
                      await updateOrderStatus({
                        user: emailid,
                        orderId: order?.ID,
                        status: "InProduction",
                        productionDate: moment().format("YYYY-MM-DD"),
                      })
                    );

                    await dispatch(
                      saveOrderData({
                        orderData: {
                          OrderSubmitToProductionDate:
                            moment().format("YYYY-MM-DD"),
                          ShirtlyOrderData: {
                            ...orderStatus,
                            grandTotalInUSD: grandTotalInUsd,
                            grandTotalInCAD: Number(
                              grandTotalInUsd * USD_TO_CAD_CONVERSION_RATE
                            ).toFixed(2),
                          },
                          Group: group,
                          StripeChargeInfo: null,
                          PaypalChargeInfo: null,
                        },
                      })
                    );

                    await dispatch(
                      orderSubmitNotification({
                        orderData: order,
                        grandTotalInUsd: Number(grandTotalInUsd),
                        grandTotalInCan: Number(
                          (
                            grandTotalInUsd * USD_TO_CAD_CONVERSION_RATE
                          ).toFixed(2)
                        ),
                        finalShippingCost,
                        shopifyId: isSamAccount
                          ? order?.OrderLines[0]?.shopifyOrderPresentedID
                          : "",
                        // taxInCad: Number(taxInCad),
                      })
                    );
                    _.isFunction(refresh) && refresh();
                  }}
                  variant="text"
                  title="SUBMIT ORDER"
                />
              ) : (
                <>
                  <div style={{ display: "block" }}>
                    <p style={{ fontSize: 16, textDecoration: "underline" }}>
                      Counterfeit Policing and IP protection Policy
                    </p>
                    <p>
                      This policy is Big Oven Tees effort to stop the production
                      of counterfeit trademark designs.
                    </p>
                    <p>
                      If a design is found to be in non-compliance with this
                      policy your order will be cancelled. If you are found to
                      continue to violate this policy after being notified, you
                      will be removed from Big Oven Tees permanently. This is a
                      zero-tolerance policy.
                    </p>
                    <p>
                      Please review your designs carefully and ensure you are
                      not using images within your designs that are a blatant
                      and direct copy of obvious trademark images. Big Oven Tees
                      will not make any concession in no circumstance moving
                      forward.
                    </p>
                    <p style={{ color: "red", fontWeight: "bold" }}>
                      If your order is in violation of this policy your order
                      will be cancelled and you will be refunded 50% of the
                      transaction.
                    </p>

                    <Checkbox
                      value={agreeTerms}
                      onChange={() => setAgreeTerms(!agreeTerms)}
                      label="I understand, agree and support this effort."
                    />
                  </div>
                  <Button
                    disabled={!agreeTerms}
                    containerStyle={{
                      backgroundColor: COLORS.GREEN,
                      color: COLORS.WHITE,
                      padding: "7px 5px",
                      fontSize: "14px",
                      lineHeight: "1.5",
                      borderRadius: "50px",
                      minWidth: "100%",
                      opacity: !agreeTerms ? 0.4 : 1,
                    }}
                    onClick={async () => {
                      await saveOrderCost();
                      setPaymentModal(true);
                    }}
                    variant="text"
                    title="CHECKOUT"
                    startIcon={<AttachMoneyIcon />}
                  />
                </>
              )
            ) : null}

            <MoreDetail>
              {orderLineItems?.map((lineItem, index) => {
                let sku = getSKUFromProductId(products, lineItem.ProductID);

                if (isSamAccount) {
                  sku = lineItem?.MarketPlaceDetails?.title;
                }

                const isProductSub = isSub(products, lineItem.ProductID);
                const printAreas = lineItem?.printAreas;
                const decorationOptional = isDecorationNotNeeded(
                  products,
                  lineItem.ProductID
                );

                const marketPlaceDetails = lineItem?.MarketPlaceDetails;
                const properties = marketPlaceDetails?.properties;
                const isPersonlised = properties?.find(
                  (p) => p?.name === "Personalized"
                );

                const logoType = properties?.find(
                  (p) => p?.name === "Logo Type"
                );

                let days = {
                  minDays: 0,
                  maxDays: 0,
                };

                const product = _.find(
                  products,
                  (p) => p?.Product?.ID === lineItem.ProductID
                );

                let fulfillmentCost = lineItem?.FulfillmentCost;

                if (auth?.profile?.printLabel) {
                  fulfillmentCost += 2;
                }

                const taxOnItem = getTaxCost(
                  taxInfo,
                  order,
                  lineItem?.Category
                );

                const taxOnFulfillment = Number(
                  ((fulfillmentCost * taxOnItem) / 100).toFixed(2)
                );

                const printType = lineItem.PrintType;

                if (product?.Product?.PrintType === printType) {
                  days = {
                    minDays: product?.Product?.defaultMinimumFulfillDays || 0,
                    maxDays: product?.Product?.defaultMaximumFulfillDays || 0,
                  };
                } else {
                  const p = product?.AdditionalTechniquesArtSettings?.find(
                    (p) => p?.name === printType
                  );
                  if (p) {
                    const { minFulfillmentDays, maxFulfillmentDays } =
                      product?.AdditionalTechniquesArtSettings?.find(
                        (p) => p?.name === printType
                      );
                    days = {
                      minDays: minFulfillmentDays || 0,
                      maxDays: maxFulfillmentDays || 0,
                    };
                  }
                }

                const availableArtworkPrintArea = printAreas?.filter(
                  (p) => p?.artworkId
                );

                const isCertificateinSku = sku?.includes("Certificate");

                const replaceImage =
                  sku?.startsWith("Not sold") &&
                  index > 0 &&
                  lineItem?.MockupSrc ===
                    "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ac/No_image_available.svg/2048px-No_image_available.svg.png";

                let certificatMockSrc = replaceImage
                  ? orderLineItems[0]?.MockupSrc
                  : lineItem?.MockupSrc;

                if (certificatMockSrc) {
                  certificatMockSrc = certificatMockSrc
                    .replace(".pdf", ".png")
                    .replace(".ai", ".png");
                }

                return (
                  <div key={`orderLineItem${index}`}>
                    {isSamAccount ? (
                      <ProductImage>
                        <Image
                          imageStyle={{ height: "auto" }}
                          src={
                            isCertificateinSku
                              ? lineItem?.Artwork
                                ? lineItem?.Artwork.replace(
                                    ".pdf",
                                    ".png"
                                  ).replace(".ai", ".png")
                                : certificatMockSrc
                              : certificatMockSrc
                          }
                          alt={lineItem.OrderLineID}
                        />
                        {isCertificateinSku && (
                          <BemerchfullButton
                            title="Upload Certificate"
                            onClick={async () => {
                              const myWidget =
                                window.cloudinary.createUploadWidget(
                                  {
                                    cloudName: "big-oven-tees-inc",
                                    show_powered_by: false,
                                    uploadPreset: "tnedst8q",
                                    sources: ["local", "camera", "url"],
                                    folder: `BOTApp/${emailid}/Certificates`,
                                    clientAllowedFormats: [
                                      "png",
                                      "jpeg",
                                      "jpg",
                                      "pdf",
                                    ],
                                  },
                                  async (error, result) => {
                                    if (
                                      !error &&
                                      result &&
                                      result.event === "success"
                                    ) {
                                      const payload = {
                                        orderId: order.ID,
                                        Marketplace: order?.Marketplace,
                                        PrintProvider: order?.PrintProvider,
                                        user: emailid,
                                        orderLineItemData: {
                                          ...lineItem,
                                          setStatus: "AwaitingPayment",
                                          Artwork: result?.info?.secure_url,
                                          MarketingImagePath:
                                            result?.info?.secure_url,
                                        },
                                      };

                                      await dispatch(addLineItem(payload));
                                      setOrder(null);
                                      myWidget.close();
                                    }
                                  }
                                );
                              myWidget.open();
                            }}
                          />
                        )}
                      </ProductImage>
                    ) : availableArtworkPrintArea?.length > 0 ? (
                      <SlickContainer>
                        <Slider {...settings}>
                          {availableArtworkPrintArea?.map((p) => (
                            <ProductImage>
                              <Image
                                imageStyle={
                                  isProductSub
                                    ? {
                                        height: "auto !important",
                                      }
                                    : { height: "auto" }
                                }
                                src={p?.MockupSrc}
                                alt={lineItem.OrderLineID}
                              />
                            </ProductImage>
                          ))}
                        </Slider>
                      </SlickContainer>
                    ) : lineItem?.aopVectorFile || decorationOptional ? (
                      <ProductImage>
                        <Image
                          imageStyle={{ height: "auto" }}
                          src={lineItem.MarketingImagePath}
                          alt={lineItem.OrderLineID}
                        />
                      </ProductImage>
                    ) : (
                      <React.Fragment>
                        {lineItem.MockupSrc && (
                          <ProductImage>
                            <Image
                              imageStyle={
                                isProductSub
                                  ? {
                                      height: "auto !important",
                                    }
                                  : { height: "auto" }
                              }
                              src={lineItem.MockupSrc}
                              alt={lineItem.OrderLineID}
                            />
                          </ProductImage>
                        )}
                        {lineItem?.MockupBackSrc && (
                          <ProductImage>
                            <Image
                              imageStyle={
                                isProductSub
                                  ? {
                                      height: "auto !important",
                                    }
                                  : { height: "auto" }
                              }
                              src={lineItem.MockupBackSrc}
                              alt={lineItem.OrderLineID}
                            />
                          </ProductImage>
                        )}
                      </React.Fragment>
                    )}

                    <ul>
                      <li>
                        <span>SKU</span>
                        <span>{sku}</span>
                      </li>
                      <li>
                        <span>COLOR</span>
                        <span>
                          {isSamAccount ? (
                            <ColorField>
                              <TextField
                                onChange={(v) => {
                                  setOrder({
                                    ...order,
                                    OrderLines: order?.OrderLines?.map((x) => {
                                      return {
                                        ...x,
                                        OrderLineItems: x?.OrderLineItems?.map(
                                          (y, a) => {
                                            if (index !== a) {
                                              return y;
                                            }
                                            return {
                                              ...y,
                                              MarketPlaceDetails: {
                                                ...y.MarketPlaceDetails,
                                                variant_title: v,
                                              },
                                            };
                                          }
                                        ),
                                      };
                                    }),
                                  });
                                }}
                                value={
                                  lineItem?.MarketPlaceDetails?.variant_title
                                }
                                size="small"
                              />
                              <CheckCircleIcon
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={async () => {
                                  const payload = {
                                    orderId: order.ID,
                                    Marketplace: order?.Marketplace,
                                    PrintProvider: order?.PrintProvider,
                                    user: emailid,
                                    orderLineItemData: {
                                      ...lineItem,
                                      setStatus: "AwaitingPayment",
                                    },
                                  };

                                  await dispatch(addLineItem(payload));
                                  setOrder(null);
                                }}
                              />
                            </ColorField>
                          ) : (
                            lineItem.Color
                          )}
                        </span>
                      </li>
                      <li>
                        <span>SIZE</span>
                        <span>
                          {isSamAccount
                            ? lineItem?.MarketPlaceDetails?.variant_title
                            : lineItem.Size}
                        </span>
                      </li>
                      <li>
                        <span>QUANTITY</span>
                        <span>
                          {isSamAccount
                            ? lineItem?.MarketPlaceDetails?.quantity
                            : lineItem.OliQuantity}
                        </span>
                      </li>

                      <li>
                        <span>DECORATION</span>

                        <span>
                          {isSamAccount ? (
                            <ColorField>
                              <select
                                value={lineItem?.PrintType}
                                onChange={(event) => {
                                  setOrder({
                                    ...order,
                                    OrderLines: order?.OrderLines?.map((x) => {
                                      return {
                                        ...x,
                                        OrderLineItems: x?.OrderLineItems?.map(
                                          (y, a) => {
                                            if (index !== a) {
                                              return y;
                                            }
                                            return {
                                              ...y,
                                              PrintType: event.target.value,
                                            };
                                          }
                                        ),
                                      };
                                    }),
                                  });
                                }}
                              >
                                {[
                                  "DTG",
                                  "EMB",
                                  "SCREENPRINT",
                                  "DURASOFT",
                                  "SUB",
                                  "AOP",
                                ]?.map((a) => (
                                  <option value={a} key={a}>
                                    {a}
                                  </option>
                                ))}
                              </select>

                              <CheckCircleIcon
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={async () => {
                                  const payload = {
                                    orderId: order.ID,
                                    Marketplace: order?.Marketplace,
                                    PrintProvider: order?.PrintProvider,
                                    user: emailid,
                                    orderLineItemData: {
                                      ...lineItem,
                                      setStatus: "AwaitingPayment",
                                    },
                                  };

                                  await dispatch(addLineItem(payload));
                                  setOrder(null);
                                }}
                              />
                            </ColorField>
                          ) : printType === "Plus" ? (
                            "TUFF PRINT TRANSFER"
                          ) : (
                            printType
                          )}
                        </span>
                      </li>

                      {!!isPersonlised && (
                        <li>
                          <span>Personalized</span>
                          <span>{isPersonlised?.value}</span>
                        </li>
                      )}

                      {!!logoType && (
                        <li>
                          <span>Logo type</span>
                          <span>{logoType?.value}</span>
                        </li>
                      )}

                      {days?.minDays && days?.maxDays ? (
                        <li>
                          <span>Decoration Fulfillment times</span>
                          <span className="glow">
                            {days?.minDays} –{days?.maxDays} business days
                          </span>
                        </li>
                      ) : null}

                      {lineItem?.DtgPlus && (
                        <>
                          <li>
                            <span>DTG PROCESS</span>
                            <span>{lineItem?.DtgPlus?.DtgPlusProcess}</span>
                          </li>
                          <li>
                            <span>DTG COLOR</span>
                            <span>
                              {lineItem?.DtgPlus?.DtgPlusProcessColor}
                            </span>
                          </li>
                        </>
                      )}
                      {lineItem?.Plus && (
                        <>
                          <li>
                            <span>TUFF PRINT TRANSFER PROCESS</span>
                            <span>{lineItem?.Plus?.DtgPlusProcess}</span>
                          </li>
                          <li>
                            <span>TUFF PRINT TRANSFER COLOR</span>
                            <span>{lineItem?.Plus?.DtgPlusProcessColor}</span>
                          </li>
                        </>
                      )}

                      {/* {isOrderAwaitingPayment && ( */}
                      <>
                        <li
                          style={{
                            borderTop: "1px solid black",
                            marginTop: 5,
                            paddingTop: 10,
                          }}
                        >
                          <span>Fulfillment Cost</span>
                          <span>
                            $
                            {currency === "USD"
                              ? fulfillmentCost
                              : (
                                  Number(fulfillmentCost) *
                                  USD_TO_CAD_CONVERSION_RATE
                                ).toFixed(2)}
                            &nbsp;
                            {currency}
                          </span>
                        </li>

                        <li>
                          <span>Tax ({taxOnItem}%)</span>
                          <span>
                            {currency === "USD"
                              ? taxOnFulfillment
                              : (
                                  Number(taxOnFulfillment) *
                                  USD_TO_CAD_CONVERSION_RATE
                                ).toFixed(2)}
                            &nbsp;
                            {currency}
                          </span>
                        </li>
                        <li>
                          <span>Final Fulfillment Cost</span>
                          <span>
                            $
                            {currency === "USD"
                              ? (fulfillmentCost + taxOnFulfillment).toFixed(2)
                              : (
                                  Number(fulfillmentCost + taxOnFulfillment) *
                                  USD_TO_CAD_CONVERSION_RATE
                                ).toFixed(2)}
                            &nbsp;{currency}
                          </span>
                        </li>
                      </>
                      {/* )} */}
                    </ul>
                  </div>
                );
              })}
            </MoreDetail>
            {/* {isOrderAwaitingPayment && ( */}
            <OrderTotal>
              <div>
                <span>Subtotal</span>
                <span>
                  $
                  {currency === "USD"
                    ? subTotal
                    : (Number(subTotal) * USD_TO_CAD_CONVERSION_RATE).toFixed(
                        2
                      )}
                  &nbsp;
                  {currency}
                </span>
              </div>

              {emailid === "support@gobardown.com" &&
                order?.PrintProvider === "CA" && (
                  <div>
                    <span>Handling Fees</span>
                    <span>${handlingCost} USD</span>
                  </div>
                )}
              <div>
                <span>
                  Shipping
                  <b>
                    &nbsp;
                    {order?.PrintProvider === "AU" ||
                    order?.PrintProvider === "EU"
                      ? ""
                      : `(${
                          emailid === "support@gobardown.com" &&
                          order?.PrintProvider === "CA"
                            ? "UPS"
                            : order?.PrintProvider === "US"
                            ? "USPS First Class Mail"
                            : order?.PrintProvider === "EU"
                            ? countries?.find(
                                (c) => c?.code2 === order?.CountryString
                              )?.region === "Europe"
                              ? "Tracked Shipping Europe"
                              : "Tracked Shipping Worldwide"
                            : order?.CountryString === "CA"
                            ? "Expedite Parcel"
                            : order?.CountryString === "US"
                            ? "Tracked Packet - USA"
                            : "Tracked Packet - International"
                        })`}
                  </b>
                </span>
                <span>
                  $
                  {currency === "USD"
                    ? shippingCost
                    : (
                        Number(shippingCost) * USD_TO_CAD_CONVERSION_RATE
                      ).toFixed(2)}
                  &nbsp;{currency}
                </span>
              </div>

              <div>
                <span>Shipping Tax ({taxPercentageOnOrder}%)</span>
                <span>
                  $
                  {currency === "USD"
                    ? shippingTaxCost
                    : (
                        Number(shippingTaxCost) * USD_TO_CAD_CONVERSION_RATE
                      ).toFixed(2)}
                  &nbsp;{currency}
                </span>
              </div>
              <div>
                <span>Final Shipping Cost</span>
                <span>
                  $
                  {currency === "USD"
                    ? finalShippingCost
                    : (
                        Number(finalShippingCost) * USD_TO_CAD_CONVERSION_RATE
                      ).toFixed(2)}
                  &nbsp;{currency}
                </span>
              </div>

              {packingMode === "eco" && (
                <div>
                  <span>Eco-Bag Cost</span>
                  <span>
                    ${currency === "USD" ? 1 : 1 * USD_TO_CAD_CONVERSION_RATE}
                    &nbsp;
                    {currency}
                  </span>
                </div>
              )}
              <div>
                <span>Total ({currency})</span>
                <span>
                  <Total>
                    $
                    {currency === "USD"
                      ? totalOrderCostInUSD
                      : (
                          Number(totalOrderCostInUSD) *
                          USD_TO_CAD_CONVERSION_RATE
                        ).toFixed(2)}
                    &nbsp;{currency}
                  </Total>
                </span>
              </div>

              {currency === "USD" && (
                <div>
                  <span>Total (CAD)</span>
                  <span>
                    <Total>
                      $
                      {(
                        totalOrderCostInUSD * USD_TO_CAD_CONVERSION_RATE
                      ).toFixed(2)}
                      &nbsp;CAD
                    </Total>
                  </span>
                </div>
              )}
            </OrderTotal>
            {/* )} */}

            {isOrderInProduction && (
              <OrderTotal>
                <div>
                  <span>Total Amount Paid</span>
                  <span>
                    <Total>
                      $
                      {(lynxOrderData?.StripeChargeInfo?.amount_captured || 0) /
                        100}
                      &nbsp;
                      {lynxOrderData?.StripeChargeInfo?.currency?.toUpperCase() ||
                        "USD"}
                    </Total>
                  </span>
                </div>
              </OrderTotal>
            )}
          </Panel>
        </OrderDetail>
        <div>
          <OrderPickUp orderDetail={order} setOrderPickUp={setOrderPickUp} />
          <OrderInformation orderDetail={order} machineStatus={machineStatus} />
          <CustomerInformation showEdit={false} orderDetail={order} />
        </div>

        {paymentModal && (
          <Stripe
            payableAmount={grandTotalInUsd}
            open={paymentModal}
            cards={cards}
            vatIncluded={isVatIncluded(taxInfo, order)}
            removeCards={async (card) => {
              const request = await dispatch(
                removeCard({
                  user: emailid,
                  cardNumber: card?.cardNumber,
                })
              );

              if (request?.error) {
                return;
              }

              setCards(undefined);
            }}
            hide={async (data) => {
              setPaymentModal(false);
              if (data) {
                let charge;
                if (data.paypal) {
                  const payload = { orderId: data.orderId };
                  charge = await dispatch(paypalCharge(payload, token));

                  if (charge.error) {
                    alert("Payment issue, please try again after sometime.");
                    return;
                  }
                } else {
                  const payload = {
                    user: emailid,
                    saveCard: data?.saveCard,
                    cardDetails: data?.cardDetails,
                    // amount: 100,
                    ID: order?.ID,
                    currency: data?.paymentInCanadian ? "CAD" : "USD",
                    ReceiptID: order?.ReceiptID,
                    amount: data.paymentInCanadian
                      ? parseInt(
                          (
                            totalOrderCostInUSD * USD_TO_CAD_CONVERSION_RATE
                          ).toFixed(2) * 100
                        )
                      : parseInt(totalOrderCostInUSD * 100),
                  };

                  charge = await dispatch(stripeCharge(payload, token));

                  if (charge.error) {
                    return;
                  }

                  if (
                    !charge ||
                    !charge?.status ||
                    charge?.status !== "succeeded" ||
                    charge?.statusCode === 402 ||
                    charge?.statusCode === 400 ||
                    charge?.type === "StripeCardError" ||
                    charge?.type === "StripeInvalidRequestError" ||
                    charge?.rawType === "card_error" ||
                    charge?.rawType === "invalid_request_error" ||
                    charge?.code === "card_declined"
                  ) {
                    setPaymentError(charge);
                    return;
                  }
                }

                const xml =
                  order?.PrintProvider === "AU" || order?.PrintProvider === "EU"
                    ? null
                    : json2xml(
                        order,
                        products,
                        emailid,
                        auth,
                        isVatIncluded(taxInfo, order),
                        data?.iossNumber
                      );

                let auPayload = {};
                let printLogisticPayload = {};

                if (order?.PrintProvider === "EU") {
                  const orderLines = order?.OrderLines[0];
                  const orderLineItems = orderLines?.OrderLineItems;

                  let artworks = [];
                  let mockups = [];
                  let placements = [];
                  let inventory_items = [];

                  for (let i = 0; i < orderLineItems.length; i++) {
                    const o = orderLineItems[i];
                    const product = products?.find(
                      (p) => p?.Product?.ID === o?.ProductID
                    );

                    const sku =
                      product?.SKUdata?.find(
                        (s) =>
                          s?.colorName === o?.Color && s?.sizeName === o?.Size
                      )?.sku || "";

                    let layouts = [];
                    for (let j = 0; j < o?.printAreas.length; j++) {
                      const p = o?.printAreas[j];
                      const {
                        designerSettings,
                        coordinates = {},
                        canvasJSON,
                      } = p;
                      const widthInInches =
                        coordinates?.width >= designerSettings?.width
                          ? designerSettings?.platenWidthInch
                          : (
                              (coordinates.width *
                                designerSettings?.multiplier) /
                              255
                            ).toFixed(1);

                      const heightInInches =
                        coordinates?.height >= designerSettings?.height
                          ? designerSettings?.platenHeightInch
                          : (
                              (coordinates.height *
                                designerSettings?.multiplier) /
                              255
                            ).toFixed(1);

                      const { multiplier } = designerSettings || {};
                      const { width, height, x = 0, y = 0 } = coordinates || {};

                      const canvasCenterPoint =
                        parseFloat(designerSettings?.width) / 2;
                      const newX = parseFloat(x);
                      const imageCenter = parseFloat(width) / 2;

                      let xPos;

                      if (newX > canvasCenterPoint) {
                        xPos =
                          canvasCenterPoint - parseFloat(width) + imageCenter;
                      } else if (newX < canvasCenterPoint) {
                        const newValue = newX + imageCenter + 1;
                        xPos =
                          newValue === canvasCenterPoint
                            ? 0
                            : newValue - canvasCenterPoint;
                        xPos = xPos === 1 ? 0 : xPos;
                      }

                      const yPos = parseInt(y);

                      let artworkSplit = p?.artwork?.split("/upload/");
                      let newArtwork = "";
                      if (artworkSplit) {
                        newArtwork = `${artworkSplit[0]}/upload/h_${Math.round(
                          heightInInches * 150
                        )},w_${Math.round(widthInInches * 150)},x_${Math.round(
                          x
                        )},y_${Math.round(y)}/${
                          artworkSplit[1]
                        }?${moment().unix()}_${p?.printAreaName}_${i}_${j}`;
                      }

                      const artwork_token = `Artwork_Token_${order?.ReceiptID}_${p?.printAreaName}_${i}_${j}`;

                      const mockup_token = `Mockup_Token_${order?.ReceiptID}_${p?.printAreaName}_${i}_${j}`;

                      const placement_token = `Placement_Token_${order?.ReceiptID}_${p?.printAreaName}_${i}_${j}`;

                      if (p?.artworkId || !!canvasJSON) {
                        layouts.push({
                          artwork_token,
                          mockup_token,
                          placement_token,
                          location: p?.printAreaName?.toLowerCase(),
                        });

                        artworks.push({
                          token: artwork_token,
                          url:
                            p?.canvasArtBoardImage ||
                            newArtwork.replace(".jpg", ".png"),
                        });
                        mockups.push({
                          token: mockup_token,
                          url: p?.MockupSrc,
                        });
                        placements.push({
                          token: placement_token,
                          x_offset: !!canvasJSON ? 0 : Math.round(xPos),
                          y_offset: !!canvasJSON ? 0 : Math.round(yPos),
                        });
                      }
                    }
                    inventory_items.push({
                      sku: sku,
                      quantity: o?.OliQuantity,
                      description: o?.Product,
                      layouts,
                    });
                  }

                  printLogisticPayload = {
                    job_id: order?.ReceiptID,
                    job_provider: "BOT",
                    special_handling: false,
                    cancelled: false,
                    inventory_items,
                    placements,
                    mockups,
                    artworks,
                    shipments: [
                      {
                        type: "Standard",
                        transport_data: [
                          {
                            company: "BOT",
                            name: order?.BuyerName,
                            surname: "",
                            address_1: order?.AddressLineOne,
                            address_2: order?.AddressLineTwo,
                            city: order?.City,
                            "post-code": order?.ZipPostal,
                            country: order?.CountryString,
                            phone: "",
                            email: order?.MasterUserEmail,
                          },
                        ],
                      },
                    ],
                  };
                } else if (order?.PrintProvider === "AU") {
                  const orderLines = order?.OrderLines[0];
                  const orderLineItems = orderLines?.OrderLineItems;

                  let inventory_items = [];

                  for (let i = 0; i < orderLineItems.length; i++) {
                    const o = orderLineItems[i];
                    const product = products?.find(
                      (p) => p?.Product?.ID === o?.ProductID
                    );

                    const sku =
                      product?.SKUdata?.find(
                        (s) =>
                          s?.colorName === o?.Color && s?.sizeName === o?.Size
                      )?.sku || "";

                    let layouts = [];
                    for (let j = 0; j < o?.printAreas.length; j++) {
                      const p = o?.printAreas[j];
                      const {
                        designerSettings,
                        coordinates = {},
                        canvasJSON,
                      } = p;
                      const widthInInches =
                        coordinates?.width >= designerSettings?.width
                          ? designerSettings?.platenWidthInch
                          : (
                              (coordinates.width *
                                designerSettings?.multiplier) /
                              255
                            ).toFixed(1);

                      const heightInInches =
                        coordinates?.height >= designerSettings?.height
                          ? designerSettings?.platenHeightInch
                          : (
                              (coordinates.height *
                                designerSettings?.multiplier) /
                              255
                            ).toFixed(1);

                      const { x = 0, y = 0 } = coordinates || {};

                      const xPos = parseInt(x * 0.0846);
                      const yPos = parseInt(y * 0.0846);

                      let artworkSplit = p?.artwork?.split("/upload/");
                      let newArtwork = "";
                      if (artworkSplit) {
                        newArtwork = `${artworkSplit[0]}/upload/h_${Math.round(
                          Number(heightInInches) * 25.4
                        )},w_${Math.round(
                          Number(widthInInches) * 25.4
                        )},x_${Math.round(x)},y_${Math.round(y)}/${
                          artworkSplit[1]
                        }?${moment().unix()}_${p?.printAreaName}_${i}_${j}`;
                      }

                      if (p?.artworkId || !!canvasJSON) {
                        layouts.push({
                          artwork:
                            p?.canvasArtBoardImage ||
                            newArtwork.replace(".jpg", ".png"),
                          preview: p?.MockupSrc,
                          side: p?.printAreaName?.toUpperCase(),
                          position: {
                            x: !!canvasJSON ? 0 : Math.round(xPos * 25.4),
                            y: !!canvasJSON ? 0 : Math.round(yPos * 25.4),
                            width: Math.round(widthInInches * 25.4),
                            height: Math.round(heightInInches * 25.4),
                          },
                        });
                      }
                    }
                    inventory_items.push({
                      sku: sku.replace("-one-size", "").toUpperCase(),
                      quantity: o?.OliQuantity,
                      id: `${
                        order?.ReceiptID + Math.floor(Math.random() * 100000)
                      }`,
                      prints: layouts,
                    });
                  }

                  auPayload = {
                    id: order?.ReceiptID,
                    addressFrom: {
                      address1: "9 Florence St",
                      address2: "",
                      city: "Teneriffe",
                      postalCode: "QLD 4005",
                      country: "Australia",
                      region: "Yuggera Country",
                      firstName: "Fulfillment",
                      lastName: "Plant",
                      email: "sales@theprintbar.com",
                      phone: "",
                    },
                    addressTo: {
                      address1: order?.AddressLineOne,
                      address2: order?.AddressLineTwo,
                      city: order?.City,
                      postalCode: order?.ZipPostal,
                      country: order?.CountryString,
                      region: order?.StateProvince,
                      firstName: order?.BuyerName?.split(" ")[0],
                      lastName:
                        order?.BuyerName?.split(" ")?.length > 1
                          ? order?.BuyerName?.split(" ")[1]
                          : " ",
                      email: order?.MasterUserEmail,
                      phone: "",
                    },
                    shipping: {
                      code:
                        order?.CountryString === "AU"
                          ? "express"
                          : "international",
                      notes: "",
                    },
                    items: inventory_items,
                  };
                }

                const totalTax =
                  shippingTaxCost + getOrderLineTax(order, taxInfo);

                const shippingWithoutTax = shippingCost;
                const fulfillmentCost = getFulfillmentCost(order);

                const submitOrderResponse =
                  order?.PrintProvider === "CA"
                    ? await dispatch(submitOrderToCanada(xml, token))
                    : order?.PrintProvider === "US"
                    ? await dispatch(submitOrderToUSA(xml, token))
                    : order?.PrintProvider === "EU"
                    ? await dispatch(
                        submitOrderToEU(printLogisticPayload, token)
                      )
                    : await dispatch(submitOrderToAU(auPayload, token));

                const parseOrderResponse =
                  order?.PrintProvider === "EU" ||
                  order?.PrintProvider === "AU" ||
                  order?.PrintProvider === "NY"
                    ? true
                    : new XMLParser().parseFromString(submitOrderResponse);

                const errorCode =
                  order?.PrintProvider === "EU" ||
                  order?.PrintProvider === "AU" ||
                  order?.PrintProvider === "NY"
                    ? null
                    : parseOrderResponse.getElementsByTagName("ErrorCode");

                if (
                  (order?.PrintProvider === "EU" &&
                    submitOrderResponse?.status !== 200) ||
                  (errorCode?.length && errorCode[0].value != 0) ||
                  (order?.PrintProvider === "AU" &&
                    submitOrderResponse?.status === "failed")
                ) {
                  alert(
                    "Error with the order, please contact Big Oven Tees for more details at info@bigoventees.com"
                  );
                  return;
                }

                const orderStatus = await dispatch(
                  await updateOrderStatus({
                    user: emailid,
                    orderId: order?.ID,
                    status: "InProduction",
                    productionDate: moment().format("YYYY-MM-DD"),
                  })
                );

                await dispatch(
                  saveOrderData({
                    orderData: {
                      OrderSubmitToProductionDate:
                        moment().format("YYYY-MM-DD"),
                      ShirtlyOrderData: {
                        ...orderStatus,
                        grandTotalInUSD: grandTotalInUsd,
                        grandTotalInCAD: Number(
                          (
                            grandTotalInUsd * USD_TO_CAD_CONVERSION_RATE
                          ).toFixed(2)
                        ),
                      },
                      AuPlantData: auPayload,
                      StripeChargeInfo: data?.paypal ? null : charge,
                      PaypalChargeInfo: data?.paypal ? charge : null,
                      Group: group,
                      grandTotalInUsd: Number(grandTotalInUsd),
                      grandTotalInCan: Number(
                        (grandTotalInUsd * USD_TO_CAD_CONVERSION_RATE).toFixed(
                          2
                        )
                      ),
                      finalShippingCost,
                      totalTax,
                      shippingWithoutTax,
                      fulfillmentCost,
                    },
                  })
                );

                await dispatch(
                  orderSubmitNotification({
                    orderData: order,
                    grandTotalInUsd: Number(grandTotalInUsd),
                    grandTotalInCan: Number(
                      (grandTotalInUsd * USD_TO_CAD_CONVERSION_RATE).toFixed(2)
                    ),
                    finalShippingCost,
                    totalTax,
                    shippingWithoutTax,
                    fulfillmentCost,
                    shopifyId: isSamAccount
                      ? order?.OrderLines[0]?.shopifyOrderPresentedID
                      : "",
                  })
                );

                const orderLineItems = order?.OrderLines[0]?.OrderLineItems;
                const isDuraSoft = orderLineItems?.find(
                  (p) => p?.PrintType === "DURASOFT"
                );
                const isAOP = orderLineItems?.find(
                  (p) => p?.PrintType === "AOP"
                );

                const isPlus = orderLineItems?.find(
                  (p) => p?.PrintType === "Plus"
                );

                if (isDuraSoft) {
                  await dispatch(
                    orderDurasoftNotification({
                      orderData: order,
                      grandTotalInUsd: Number(grandTotalInUsd),
                      grandTotalInCan: Number(
                        (grandTotalInUsd * USD_TO_CAD_CONVERSION_RATE).toFixed(
                          2
                        )
                      ),
                      finalShippingCost,
                      // taxInCad: Number(taxInCad),
                    })
                  );
                }

                if (isPlus) {
                  await dispatch(
                    orderTuffPrintTransferNotification({
                      orderData: order,
                      grandTotalInUsd: Number(grandTotalInUsd),
                      grandTotalInCan: Number(
                        (grandTotalInUsd * USD_TO_CAD_CONVERSION_RATE).toFixed(
                          2
                        )
                      ),
                      finalShippingCost,
                      // taxInCad: Number(taxInCad),
                    })
                  );
                }

                if (isAOP) {
                  await dispatch(
                    orderAOPNotification({
                      orderData: order,
                      grandTotalInUsd: Number(grandTotalInUsd),
                      grandTotalInCan: Number(
                        (grandTotalInUsd * USD_TO_CAD_CONVERSION_RATE).toFixed(
                          2
                        )
                      ),
                      finalShippingCost,
                      // taxInCad: Number(taxInCad),
                    })
                  );
                }

                setPaymentModal(false);
                _.isFunction(refresh) && refresh();
              } else {
                setPaymentModal(false);
              }
            }}
          />
        )}

        {paymentError && (
          <PaymentErrorDialog
            error={paymentError}
            open={!!paymentError}
            setOpen={() => setPaymentError(undefined)}
          />
        )}
      </Container>
    </>
  );
};

export default OrderSummary;
